// eslint-disable-next-line jsdoc/require-jsdoc
import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import usePlan, { DevicePlan } from '../hooks/usePlan';
import { isAdminRole } from '../services/session-storage-service';

// eslint-disable-next-line jsdoc/require-jsdoc
function deviceCount(count: number): string {
  return `${count} device${count !== 1 ? 's' : ''}`;
}

// eslint-disable-next-line jsdoc/require-jsdoc
function renewUrl(plans: DevicePlan[]): string {
  return `/pricing?devices=${plans.map((p) => p.serialNumber).join(',')}`;
}

/**
 * Global banner shown when any device's plan is expiring soon or in its grace period.
 * @returns Banner element or null.
 */
export default function PlanExpiryBanner(): ReactElement | null {
  const { devicePlans } = usePlan();
  const navigate = useNavigate();
  // Only an Admin can renew; everyone else is told who to ask.
  const canRenew = isAdminRole();

  const inGrace = devicePlans.filter(
    (p) => p.planStatus === 'grace-period' && p.graceDaysRemaining !== null
  );
  const expiringSoon = devicePlans.filter(
    (p) => p.planStatus === 'expiring-soon' && p.daysUntilExpiry !== null
  );

  if (inGrace.length) {
    // The device closest to dropping to Free sets the deadline.
    const soonest = Math.min(...inGrace.map((p) => p.graceDaysRemaining as number));
    const single = inGrace.length === 1 ? inGrace[0] : null;
    return (
      <div className="flex items-center justify-between gap-4 bg-error-500/10 border-b border-error-500/30 px-6 py-2.5 text-sm text-error-300">
        <div className="flex items-center gap-2">
          <span className="material-icons-outlined text-base leading-none">error</span>
          <span>
            {single ? (
              <>
                The <span className="font-semibold">{single.subscribedPlan}</span> plan for{' '}
                <span className="font-semibold">{single.deviceName}</span> has expired.{' '}
              </>
            ) : (
              <>
                Plans for <span className="font-semibold">{deviceCount(inGrace.length)}</span> have
                expired.{' '}
              </>
            )}
            {soonest > 0 ? (
              <>
                {single ? 'It' : 'They'} will move to the{' '}
                <span className="font-semibold">Free plan</span> in{' '}
                <span className="font-semibold">
                  {soonest} day{soonest !== 1 ? 's' : ''}
                </span>{' '}
                unless renewed{canRenew ? '' : ' by your Admin'}.
              </>
            ) : (
              <>{single ? 'It is' : 'They are'} being moved to the Free plan.</>
            )}
          </span>
        </div>
        {canRenew && (
          <button
            type="button"
            onClick={() => navigate(renewUrl(inGrace))}
            className="shrink-0 rounded border border-error-400 px-3 py-1 text-xs font-semibold text-error-300 hover:bg-error-500/20"
          >
            Renew Now
          </button>
        )}
      </div>
    );
  }

  if (expiringSoon.length) {
    const soonest = Math.min(...expiringSoon.map((p) => p.daysUntilExpiry as number));
    const single = expiringSoon.length === 1 ? expiringSoon[0] : null;
    return (
      <div className="flex items-center justify-between gap-4 bg-warning-500/10 border-b border-warning-500/30 px-6 py-2.5 text-sm text-warning-300">
        <div className="flex items-center gap-2">
          <span className="material-icons-outlined text-base leading-none">warning</span>
          <span>
            {single ? (
              <>
                The <span className="font-semibold">{single.subscribedPlan}</span> plan for{' '}
                <span className="font-semibold">{single.deviceName}</span> expires in{' '}
              </>
            ) : (
              <>
                Plans for <span className="font-semibold">{deviceCount(expiringSoon.length)}</span>{' '}
                expire within{' '}
              </>
            )}
            <span className="font-semibold">
              {soonest} day{soonest !== 1 ? 's' : ''}
            </span>
            . After expiry there is a 10-day grace period before a device moves to the Free plan.
            {canRenew ? '' : ' Ask your Admin to renew.'}
          </span>
        </div>
        {canRenew && (
          <button
            type="button"
            onClick={() => navigate(renewUrl(expiringSoon))}
            className="shrink-0 rounded border border-warning-400 px-3 py-1 text-xs font-semibold text-warning-300 hover:bg-warning-500/20"
          >
            Renew Now
          </button>
        )}
      </div>
    );
  }

  return null;
}
